import { useEffect, useState } from 'react'

import { api } from '@/lib/api'

/**
 * A list from the Jira cache (/users, /projects) filtered by a debounced query.
 * null — loading; 'error' — the request failed or the answer is not a list (the page shows it, never crashes).
 * `extraQuery` (e.g. `live=1`) is appended to every request.
 */
export function useCachedList<T>(path: string, q: string, debounceMs = 300, extraQuery = ''): T[] | 'error' | null {
  const [debounced, setDebounced] = useState(q)
  const [rows, setRows] = useState<T[] | 'error' | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), debounceMs)
    return () => clearTimeout(t)
  }, [q, debounceMs])

  useEffect(() => {
    let live = true
    const parts = [debounced ? `q=${encodeURIComponent(debounced)}` : '', extraQuery].filter(Boolean)
    const qs = parts.length ? `?${parts.join('&')}` : ''
    setRows(null)
    api<unknown>(`${path}${qs}`)
      .then((r) => { if (live) setRows(Array.isArray(r) ? (r as T[]) : 'error') })
      .catch(() => { if (live) setRows('error') })
    return () => { live = false }
  }, [path, debounced, extraQuery])

  return rows
}
